import React from "react";
import {
  SectionConfig,
  TableConfig,
  FieldConfig,
  TableColumnConfig,
  DisplayLabelContext,
  DynamicLabel,
  CalibrationDisplayGroup,
} from "./types";
import {
  formatPressureByUnit,
  formatPressureMPa,
  formatPressureKPa,
} from "./formatters";

const resolveDynamicLabel = (
  label: DynamicLabel | undefined,
  ctx: DisplayLabelContext
): string => {
  if (label === undefined) return "";
  return typeof label === "function" ? label(ctx) : label;
};

/**
 * Get nested value from object using dot notation
 * e.g., "abc_coefficients.A" returns data.abc_coefficients.A
 */
function getNestedValue(obj: any, path: string): any {
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
}

/**
 * Render a single field
 */
interface FieldRendererProps {
  field: FieldConfig;
  data: any;
  labelContext: DisplayLabelContext;
}

const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  data,
  labelContext,
}) => {
  const value = getNestedValue(data, field.key);

  // Don't render if value doesn't exist and no format function is provided
  if (value === undefined || value === null) {
    return null;
  }

  const displayValue = field.format ? field.format(value) : value;
  const unitLabel = resolveDynamicLabel(field.unit, labelContext);

  return (
    <div>
      <label className="block text-xs font-medium text-gray-500">
        {resolveDynamicLabel(field.label, labelContext)}
      </label>
      <p className="text-sm text-gray-900 font-medium">
        {displayValue}
        {unitLabel && ` ${unitLabel}`}
      </p>
    </div>
  );
};

/**
 * Render a section with multiple fields
 */
interface SectionRendererProps {
  section: SectionConfig;
  data: any;
  labelContext: DisplayLabelContext;
}

export const SectionRenderer: React.FC<SectionRendererProps> = ({
  section,
  data,
  labelContext,
}) => {
  // Check if section should be displayed
  if (section.condition && !section.condition(data)) {
    return null;
  }

  // Filter fields that have values or format functions
  const visibleFields = section.fields.filter((field: FieldConfig) => {
    if (field.condition && !field.condition(data)) {
      return false;
    }
    const value = getNestedValue(data, field.key);
    return value !== undefined && value !== null;
  });

  // Don't render section if no fields are visible
  if (visibleFields.length === 0) {
    return null;
  }

  return (
    <div className={`${section.bgColor} p-4 rounded-lg`}>
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        {section.title}
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {visibleFields.map((field: FieldConfig, index: number) => (
          <FieldRenderer
            key={index}
            field={field}
            data={data}
            labelContext={labelContext}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Render a cycle badge with arrow indicators (used in tables)
 */
const CycleBadge: React.FC<{ cycle: string }> = ({ cycle }) => {
  const cycleValue = cycle?.toLowerCase();
  const isIncrement = cycleValue === "increment" || cycleValue === "up";
  const isDecrement = cycleValue === "decrement" || cycleValue === "down";
  
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
        isIncrement
          ? "bg-blue-100 text-blue-800"
          : isDecrement
          ? "bg-orange-100 text-orange-800"
          : "bg-gray-100 text-gray-800"
      }`}
    >
      {isIncrement && <span>↑</span>}
      {isDecrement && <span>↓</span>}
      {cycle}
    </span>
  );
};

/**
 * Render a table cell
 */
interface TableCellProps {
  column: TableColumnConfig;
  row: any;
  labelContext: DisplayLabelContext;
}

const TableCell: React.FC<TableCellProps> = ({
  column,
  row,
  labelContext,
}) => {
  const value = getNestedValue(row, column.key);
  
  // Handle special case for cycle column - render badge even for empty values
  if (column.key === "cycle") {
    return (
      <td className="px-3 py-2 text-sm">
        {value ? (
          <CycleBadge cycle={value} />
        ) : (
          <span className="text-gray-400 text-xs">N/A</span>
        )}
      </td>
    );
  }

  let displayValue: string | number | undefined | null =
    column.format ? column.format(value) : value;

  if (
    column.format &&
    (column.format === formatPressureMPa ||
      column.format === formatPressureKPa)
  ) {
    displayValue = formatPressureByUnit(value, labelContext.measurementUnit);
  }

  const cellValue =
    displayValue !== undefined && displayValue !== null
      ? displayValue
      : "N/A";

  return (
    <td className={`px-3 py-2 text-sm text-gray-900 ${column.className || ""}`}>
      {cellValue}
      {column.unit && cellValue !== "N/A" && column.unit}
    </td>
  );
};

/**
 * Render a table with data
 */
interface TableRendererProps {
  table: TableConfig;
  data: any;
  labelContext: DisplayLabelContext;
}

const renderDataTable = (
  columns: TableColumnConfig[],
  rows: any[],
  table: TableConfig,
  labelContext: DisplayLabelContext,
) => (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-100">
        <tr>
          {columns.map((column: TableColumnConfig, index: number) => (
            <th
              key={index}
              className={`px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap ${column.preserveLabelCase ? "" : "uppercase"}`}
            >
              {resolveDynamicLabel(column.label, labelContext)}
            </th>
          ))}
        </tr>
      </thead>

      <tbody className="bg-white divide-y divide-gray-200">
        {rows.map((row: any, rowIndex: number) => {
          const rowClassName = table.rowClassName
            ? table.rowClassName(row, rowIndex)
            : "";

          return (
            <tr key={rowIndex} className={rowClassName}>
              {columns.map((column: TableColumnConfig, colIndex: number) => (
                <TableCell
                  key={colIndex}
                  column={column}
                  row={row}
                  labelContext={labelContext}
                />
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export const TableRenderer: React.FC<TableRendererProps> = ({
  table,
  data,
  labelContext,
}) => {
  // Check if table should be displayed
  if (table.condition && !table.condition(data)) {
    return null;
  }

  const primaryData = getNestedValue(data, table.dataKey);

  // Don't render if no data
  if (!primaryData || !Array.isArray(primaryData) || primaryData.length === 0) {
    return null;
  }

  let tableData = primaryData;
  if (table.secondaryDataKey && table.mergeOn && table.mergeOn.length > 0) {
    const secondaryData = getNestedValue(data, table.secondaryDataKey);
    if (Array.isArray(secondaryData) && secondaryData.length > 0) {
      const buildKey = (row: any) =>
        table.mergeOn!.map((key) => String(row?.[key])).join("|");
      const secondaryByKey = new Map<string, any>();
      secondaryData.forEach((row) => secondaryByKey.set(buildKey(row), row));
      tableData = primaryData.map((row: any) => ({
        ...row,
        ...secondaryByKey.get(buildKey(row)),
      }));
    }
  }

  const groupByKey = table.groupByKey;
  const columns = groupByKey
    ? table.columns.filter((column) => column.key !== groupByKey)
    : table.columns;

  if (groupByKey) {
    const groups: { value: any; rows: any[] }[] = [];
    const indexByValue = new Map<string, number>();

    for (const row of tableData) {
      const value = row?.[groupByKey];
      const mapKey = String(value);
      const existing = indexByValue.get(mapKey);
      if (existing === undefined) {
        indexByValue.set(mapKey, groups.length);
        groups.push({ value, rows: [row] });
      } else {
        groups[existing].rows.push(row);
      }
    }

    return (
      <div className={`${table.bgColor} p-4 rounded-lg space-y-4`}>
        <h3 className="text-lg font-medium text-gray-900">{table.title}</h3>
        {groups.map((group, groupIndex) => (
          <div key={`${String(group.value)}-${groupIndex}`} className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-800">
              {table.groupTitle
                ? table.groupTitle(group.value)
                : `Channel ${group.value}`}
            </h4>
            {renderDataTable(columns, group.rows, table, labelContext)}
          </div>
        ))}
      </div>
    );
  }
  
  return (
    <div className={`${table.bgColor} p-4 rounded-lg`}>
      <h3 className="text-lg font-medium text-gray-900 mb-4">{table.title}</h3>
      {renderDataTable(columns, tableData, table, labelContext)}
    </div>
  );
};

/**
 * Main component to render all sections and tables
 */
interface DynamicCalibrationDisplayProps {
  sections?: SectionConfig[];
  tables?: TableConfig[];
  data: any;
  /** From sensor.sensor_type.unit (e.g. MPa, kPa); drives pressure labels & decimals. */
  measurementUnit?: string | null;
  /**
   * Restrict rendering to sections/tables tagged with one of these groups
   * (a missing `group` on a config entry is treated as "setup"). Omit to
   * render everything, preserving the original behavior.
   */
  groups?: CalibrationDisplayGroup[];
}

export const DynamicCalibrationDisplay: React.FC<
  DynamicCalibrationDisplayProps
> = ({ sections, tables, data, measurementUnit, groups }) => {
  if (!data) {
    return null;
  }

  const labelContext: DisplayLabelContext = {
    measurementUnit: (measurementUnit && measurementUnit.trim()) || "MPa",
  };

  const matchesGroups = (group: CalibrationDisplayGroup | undefined) =>
    !groups || groups.includes(group || "setup");

  const visibleSections = sections?.filter((section) => matchesGroups(section.group));
  const visibleTables = tables?.filter((table) => matchesGroups(table.group));

  return (
    <div className="space-y-6">
      {/* Render sections */}
      {visibleSections?.map((section, index) => (
        <SectionRenderer
          key={`section-${index}`}
          section={section}
          data={data}
          labelContext={labelContext}
        />
      ))}

      {/* Render tables */}
      {visibleTables?.map((table, index) => (
        <TableRenderer
          key={`table-${index}`}
          table={table}
          data={data}
          labelContext={labelContext}
        />
      ))}
    </div>
  );
};

export default DynamicCalibrationDisplay;

